/**
 * Helper functions for KLineChart Financial Astrology Implementation
 * These functions are adapted from the original TradingView implementation
 * @version 1.1.1 - Fixed super prediction line (helio) to respect startingPosition as a polarity flag
 */

import { cloneDeep, isEmpty } from 'lodash';
import { MODEL_TYPES } from "helpers/constants";
import moment from 'moment';

// ============================================
// Aspect Degree Calculations
// ============================================

/**
 * Get the degree value for an aspect type
 */
export const aspectDegree = (aspect, isRepeat = false) => {
  let output = 0;
  switch (aspect) {
    default:
    case "conjunction":
      output = 0;
      if (isRepeat) {
        output = output + 360;
      }
      break;
    case "opposition":
      output = 180;
      break;
    case "square":
      output = 90;
      break;
    case "trine":
      output = 120;
      break;
    case "sextile":
      output = 60;
      break;
    case "inconjunct":
      output = 150;
      break;
    case "semisextile":
      output = 30;
      break;
    case "semisquare":
      output = 45;
      break;
    case "sesquiquadrate":
      output = 135;
      break;
    case "quintile":
      output = 72;
      break;
    case "biquintile":
      output = 144;
      break;
    case "semiquintile":
      output = 36;
      break;
  }
  return output;
};

/**
 * Calculate aspect degree
 */
export const calcAspectDegree = (degree, aspect, isRepeat = false, isReverse = false) => {
  if (!aspect) {
    return degree;
  }
  let output = null;
  if (isReverse) {
    output = parseFloat(degree) - aspectDegree(aspect, isRepeat);
  } else {
    output = parseFloat(degree) + aspectDegree(aspect, isRepeat);
  }
  return output || 0;
};

// ============================================
// Price/Longitude Conversion
// ============================================

export const convertLongitudeToPrice = (longitude, conversionFactor) => {
  return longitude * conversionFactor;
};

// ============================================
// Degree Smoothing (Critical for 0°/360° boundary)
// ============================================

export function smoothDegrees(timeDegrees) {
  const threshold = 100;
  const entries = Object.entries(timeDegrees);
  const smoothedDegrees = {};
  let lastChangeDegree = 0;

  for (let i = 0; i < entries.length; i++) {
    let [time, degree] = entries[i];
    degree = parseFloat(degree);
    let finalDegree = lastChangeDegree + degree;

    if (!entries[i + 1]) {
      smoothedDegrees[time] = finalDegree.toFixed(4);
      continue;
    }

    let [nextTime, nextDegree] = entries[i + 1];
    nextDegree = parseFloat(nextDegree);
    let change = degree - nextDegree;

    if (change > threshold) {
      lastChangeDegree += degree;
      finalDegree = lastChangeDegree;
    } else if (change < (threshold * -1)) {
      finalDegree = lastChangeDegree || degree;
      lastChangeDegree -= nextDegree;
      degree = nextDegree - degree;
    }

    smoothedDegrees[time] = finalDegree.toFixed(4).toString();
  }

  return smoothedDegrees;
}

export const convertDegreesToPrice = (degree, aspect, conversionFactor) => {
  // Step 1: Apply aspect degree adjustment
  const adjustedDegree = calcAspectDegree(degree, aspect);

  // Step 2: Convert to price
  const price = convertLongitudeToPrice(adjustedDegree, conversionFactor);

  return Number(price.toFixed(2));
};



// ============================================
// Planet Line Data Calculation - FIXED
// ============================================

/**
 * Calculate planet lines data from time degrees
 * FIXED: Case-insensitive planet name lookup
 */
export const calculatePlanetLinesData = (
  planets,
  timeDegreesData,
  calendar,
  graph,
  graphOrigTimeRange
) => {
  let timeDegrees = cloneDeep(timeDegreesData);

  if (isEmpty(timeDegrees) || !graph?.length || !graphOrigTimeRange?.start) {
    console.log('calculatePlanetLinesData: Missing required data', {
      hasTimeDegrees: !isEmpty(timeDegrees),
      graphLength: graph?.length,
      graphOrigTimeRange
    });
    return null;
  }

  // Create a case-insensitive lookup map for timeDegrees
  const timeDegreesLowerCase = {};
  Object.keys(timeDegrees).forEach(key => {
    timeDegreesLowerCase[key.toLowerCase()] = timeDegrees[key];
  });

  console.log('Available planet keys in timeDegrees:', Object.keys(timeDegreesLowerCase));

  const nextPlanetsData = {};

  planets.forEach((planet) => {
    if (!planet.enabled) {
      return;
    }

    const planetName = planet.name;
    const planetKey = planet.key;
    const planetAspect = planet.aspect;

    // FIXED: Case-insensitive lookup
    const planetNameLower = planetName.toLowerCase();
    const planetDegrees = timeDegreesLowerCase[planetNameLower];

    if (isEmpty(planetDegrees)) {
      console.warn('Planet degrees not found for:', planetName, '(lookup key:', planetNameLower, ')');
      console.log('Available keys:', Object.keys(timeDegreesLowerCase));
      return;
    }

    console.log(`Found degrees for ${planetName}:`, Object.keys(planetDegrees).length, 'entries');

    const generatePlanetLines = (degrees) => {
      if (isEmpty(degrees)) return;

      // Apply smoothing to handle 0°/360° boundary
      let output = smoothDegrees(degrees);

      // Apply aspect calculation to each degree
      for (let time in output) {
        output[time] = calcAspectDegree(output[time], planetAspect);
      }

      if (!nextPlanetsData[planetKey]) {
        nextPlanetsData[planetKey] = [];
      }
      nextPlanetsData[planetKey].push(output);
    };

    generatePlanetLines(planetDegrees);
  });

  console.log('calculatePlanetLinesData result:', Object.keys(nextPlanetsData));
  return nextPlanetsData;
};

// ============================================
// Reverse Planet Line
// ============================================

export const reversePlanetLine = (timeDegrees) => {
  const timestamps = Object.keys(timeDegrees);
  const values = Object.values(timeDegrees);

  const oppositeData = {};
  timestamps.forEach((timestamp, index) => {
    oppositeData[timestamp] = 360 - values[index];
  });

  return oppositeData;
};

// ============================================
// Trend Line Projection
// ============================================

export const calculateTrendLineProjection = (trendLineData, graphData, chartTimeRange, startingPosition, startDate, highestPriceChange, model, trendlineTimeRange) => {
  try {
    if (isEmpty(trendLineData)) {
      return null;
    }

    if (isEmpty(graphData) || isEmpty(chartTimeRange)) {
      throw new Error("Invalid data supplied");
    }

    console.log(chartTimeRange, 'count kline');

    const projection = [];

    // Step 1: Find the starting graph day
    let graphDay = graphData.find(row =>
      moment(row.timestamp).isSameOrAfter(moment(startDate), "day")
    );

    // Fallback to first entry if no matching day found
    if (!graphDay) {
      graphDay = graphData[0];
    }

    // Override to last entry when trendlineTimeRange is provided
    if (trendlineTimeRange) {
      graphDay = graphData[graphData.length - 1];
    }

    // Final validation
    if (!graphDay) {
      throw new Error("Invalid graph start date");
    }

    const { close } = graphDay;

    // Initial trend direction: 1 = up, 0 = down
    let trendDirection = startingPosition === "high" ? 1 : 0;
    let price = close;
    let priceIncrement = parseFloat(highestPriceChange) || 0;
    if (model === "helio") {
      priceIncrement = priceIncrement * 0.25; // Reduces the "Super" line's slope by 85%
    }
    // Step 2: Process each timestamp in trendLineData
    for (let timestamp in trendLineData) {
      // Time filtering: bypass when trendlineTimeRange is set, otherwise filter by chartTimeRange
      const shouldProcess = trendlineTimeRange ||
        moment.unix(timestamp).utc().isSameOrBefore(moment.unix(chartTimeRange.to));

      if (shouldProcess) {
        const data = trendLineData[timestamp];

        // Step 3: Determine trend direction based on model type
        if (model === MODEL_TYPES.helio) {
          // Heliocentric (Super Prediction Line):
          // Server-provided direction drives the line, BUT `startingPosition`
          // acts as a POLARITY flag so that toggling high/low visibly mirrors
          // the line (same user-facing behavior as geo):
          //   "high" -> server "up" means price-up,   "down" means price-down (natural)
          //   "low"  -> server "up" means price-down, "down" means price-up   (inverted)
          // If server trendDirection is missing, the previous direction is kept.
          const highPolarity = startingPosition === "high";
          if (data?.trendDirection === "up") {
            trendDirection = highPolarity ? 1 : 0;
          } else if (data?.trendDirection === "down") {
            trendDirection = highPolarity ? 0 : 1;
          }
          // else: preserve current direction
        } else {
          // Geocentric (default): Use checkpoint-based reversal
          if (data?.isCheckpoint) {
            trendDirection = trendDirection === 1 ? 0 : 1;
          }
        }

        // Step 4: Add point to projection if day settings allow
        if (data?.daySetting) {
          projection.push({
            time: Number(timestamp),
            price: parseFloat(price.toFixed(2))
          });
        }

        // Step 5: Update price for next iteration
        price = trendDirection === 1 ? price + priceIncrement : price - priceIncrement;
      }
    }

    console.log("projection klineview", projection);

    return projection;
  } catch (err) {
    console.error("Error creating trendline projection:", err);
    return null;
  }
}

// ============================================
// Range Checking Utilities
// ============================================

export function isPointWithinTimeAndPriceRange(
  time,
  price,
  timeRangeFrom,
  timeRangeTo,
  priceRangeFrom,
  priceRangeTo
) {
  const timeMoment = moment.unix(time);
  const fromMoment = moment.isMoment(timeRangeFrom) ? timeRangeFrom : moment.unix(timeRangeFrom);
  const toMoment = moment.isMoment(timeRangeTo) ? timeRangeTo : moment.unix(timeRangeTo);

  const isWithinTime = timeMoment.isSameOrAfter(fromMoment) && timeMoment.isSameOrBefore(toMoment);
  const isWithinPrice = price >= priceRangeFrom && price <= priceRangeTo;

  return isWithinTime && isWithinPrice;
}

// ============================================
// Graph Data Utilities
// ============================================

export function addDaysToGraph(graph, numDays) {
  let graphClone = cloneDeep(graph);
  let firstGraphTimestamp = moment(graph[0].timestamp);

  let startTimestamp = firstGraphTimestamp.clone().subtract(numDays, 'days');
  let previousBars = [];

  while (startTimestamp.isBefore(firstGraphTimestamp)) {
    previousBars.push({
      timestamp: startTimestamp.toISOString(),
      low: 0,
      high: 0,
      open: 0,
      close: 0,
      volume: 0,
      change: 0,
    });
    startTimestamp = startTimestamp.add(1, 'day');
  }

  let lastGraphTimestamp = moment(graph[graph.length - 1].timestamp);
  let endTimestamp = lastGraphTimestamp.clone().add(numDays, 'days');
  let nextBars = [];

  while (endTimestamp.isAfter(lastGraphTimestamp)) {
    nextBars.unshift({
      timestamp: endTimestamp.toISOString(),
      low: 0,
      high: 0,
      open: 0,
      close: 0,
      volume: 0,
      change: 0,
    });
    endTimestamp = endTimestamp.subtract(1, 'day');
  }

  return [...previousBars, ...graphClone, ...nextBars];
}

// ============================================
// Tooltip Management
// ============================================

export const displayTooltip = (content, x, y) => {
  removeTooltip();

  const tooltip = document.createElement('div');
  tooltip.classList.add('kline-custom-tooltip');
  tooltip.style.cssText = `
    position: fixed;
    left: ${x + 10}px;
    top: ${y + 10}px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 9999;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    max-width: 250px;
  `;
  tooltip.innerHTML = content;

  document.body.appendChild(tooltip);
};

export const removeTooltip = () => {
  const existingTooltip = document.querySelector('.kline-custom-tooltip');
  if (existingTooltip) {
    document.body.removeChild(existingTooltip);
  }
};

// ============================================
// Timestamp Utilities
// ============================================

export const isTimestampMilliseconds = (timestamp) => {
  return timestamp > 10000000000;
};

export function timeStampRange(start, end, max, min, unix = false) {
  // Set max and min to end and start respectively if they are not provided
  if (!max) {
    max = end;
  }
  if (!min) {
    min = start;
  }

  // Convert timestamps to Moment.js objects in UTC
  let startDate = moment.unix(start).utc();
  let endDate = moment.unix(end).utc();
  const maxDate = moment.unix(max).utc();
  const minDate = moment.unix(min).utc();

  // Adjust startDate and endDate based on min and max limits
  if (startDate.isBefore(minDate, "day")) {
    startDate = minDate.clone();
  }
  if (endDate.isAfter(maxDate, "day")) {
    endDate = maxDate.clone();
  }

  // Initialize an array to hold the range of dates
  let dateRange = [];

  // Loop from startDate to endDate, incrementing by 1 day each iteration
  let currentDate = startDate.clone();
  while (currentDate.isSameOrBefore(endDate, "day")) {
    if (unix) {
      dateRange.push(currentDate.clone().startOf("day").unix());
    } else {
      dateRange.push(currentDate.clone().startOf("day").toDate());
    }
    currentDate.add(1, 'days');
  }

  return dateRange;
}

export const mergeTimestamps = (arr1, arr2) => {
  const merged = [...new Set([...arr1, ...arr2])];
  return merged.sort((a, b) => a - b);
};

export const sortTimestamps = (timestamps) => {
  return timestamps.sort((a, b) => a - b);
};

export const mergeDegrees = (existing, newPoints) => {
  const merged = [...existing];

  newPoints.forEach(newPoint => {
    const exists = merged.some(
      p => p.time === newPoint.time && Math.abs(p.price - newPoint.price) < 0.01
    );
    if (!exists) {
      merged.push(newPoint);
    }
  });

  return merged;
};

// ============================================
// Format Utilities
// ============================================

export const formatString = (str) => {
  if (!str) return '';
  return str
    .split(/[_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const calculateFutureDate = (date, maxFutureDate) => {
  const currentDate = moment(date);
  const futureDateLimit = moment(maxFutureDate);

  // If maxFutureDate is in the future relative to the provided date, return maxFutureDate; otherwise return the date itself
  return futureDateLimit.isAfter(currentDate) ? futureDateLimit : currentDate;
};

export const calculatePastDate = (baseTimestamp, pastDays) => {
  const past = moment(pastDays);
  return past.toDate();
};